import {
  isEmpty,
  isUserId,
  Logger,
  type AccountData,
  type AccountId,
  type AccountListSuccess,
  type UserData,
} from '@terros/common'
import { useQueryClient, type InfiniteData } from '@tanstack/react-query'
import { accountKey, userKey } from '../keys'

const logger = new Logger('useAccountCache')
// logger.level = 'verbose'

type AccountCache = {
  addItem: (item: AccountData) => void
  getItem: (accountId: AccountId) => AccountData | undefined
  removeItem: (accountId: AccountId) => void
  updateItem: (account: AccountData) => void
  invalidateItem: (accountId: AccountId) => void
  clearList: VoidFunction
}

export function useAccountCache(): AccountCache {
  const queryClient = useQueryClient()
  return {
    getItem: (accountId: AccountId): AccountData | undefined => {
      const cached = queryClient.getQueryData<AccountData>(accountKey(accountId))
      if (cached) return cached

      const queries = queryClient.getQueriesData<AccountData[]>({ queryKey: ['accounts'] })
      for (const [, data] of queries) {
        if (isEmpty(data)) continue
        const found = data.find((a) => a.accountId === accountId)
        if (found) return found
      }
    },
    clearList: () => {
      logger.verbose('clearList accounts')
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
      queryClient.invalidateQueries({ queryKey: ['accounts-infinite'] })
    },
    addItem: (item: AccountData) => {
      logger.verbose('addItem account', item)
      const ownerId = item.ownerId
      if (ownerId) {
        if (isUserId(ownerId)) {
          const userData = queryClient.getQueryData<UserData>(userKey(ownerId))
          if (userData) {
            item = { ...item, owner: userData }
          }
        }
      }
    },
    removeItem: (accountId: AccountId) => {
      logger.verbose('removeItem account', accountId)
      queryClient.setQueriesData<AccountData[]>({ queryKey: ['accounts'] }, (old) =>
        old?.filter((t) => t.accountId !== accountId)
      )
      queryClient.setQueriesData<InfiniteData<AccountListSuccess>>({ queryKey: ['accounts-infinite'] }, (old) => {
        if (!old?.pages) return
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            accounts: page.accounts.filter((t) => t.accountId !== accountId),
          })),
        }
      })
    },
    updateItem: (account: AccountData) => {
      logger.verbose('updateItem account', account)
      queryClient.setQueriesData<AccountData[]>({ queryKey: ['accounts'] }, (old) => {
        return old?.map((t) => (t.accountId === account.accountId ? account : t))
      })
      queryClient.setQueriesData<InfiniteData<AccountListSuccess>>({ queryKey: ['accounts-infinite'] }, (old) => {
        if (!old?.pages) return
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            accounts: page.accounts.map((t) => {
              if (t.accountId !== account.accountId) return t
              if (t.locationId === account.locationId)
                return {
                  ...account,
                  location: account.location || t.location,
                }
              return account
            }),
          })),
        }
      })
      queryClient.setQueryData<AccountData>(accountKey(account.accountId), account)
    },
    invalidateItem: (accountId: AccountId) => {
      queryClient.invalidateQueries({ queryKey: accountKey(accountId) })
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
      queryClient.invalidateQueries({ queryKey: ['accounts-infinite'] })
    },
  }
}
